/**
 * The Person view (prototype `person-page`, direction B): a sticky dossier
 * beside the civic record. `from` and `race` add a Return to Election link
 * and never change which Person the URL names (Website ADR 0003).
 */
import type { PublicPersonPage, PublicSeatAssignmentSection } from "../api";
import type { ToolContext, ViewRenderer } from "../context";
import { h } from "../dom";
import { directoryHref, personSlug } from "../links";
import { localToday } from "../model";
import { heldOfficeTitle, personName, timelineEntries } from "../person-model";
import { buildUrl, type View } from "../router";
import type { MyBallotSnapshot } from "../my-ballot-state";
import { openComparison } from "../components/comparison";
import { renderDossier } from "../components/dossier";
import { renderState } from "../components/states";
import { renderTimeline } from "../components/timeline";
import { getProfile, warnOrphans } from "../webflow-content";

type PersonView = Extract<View, { kind: "person" }>;

function returnLink(
  view: PersonView,
  page: URL,
  myBallot: MyBallotSnapshot | null
): HTMLElement | null {
  if (!view.from) return null;
  const href =
    myBallot && myBallot.cycle === view.from
      ? buildUrl(
          {
            kind: "election",
            cycle: myBallot.cycle,
            ballot: myBallot.ballot,
            race: myBallot.priorRace,
          },
          page,
          myBallot.priorFilters
        )
      : buildUrl(
          { kind: "election", cycle: view.from, ballot: null, race: view.race },
          page
        );
  return h("a", { class: "bsc-return", href }, h("span", { "aria-hidden": "true" }, "← "), "Return to Election");
}

/**
 * Below 992px the dossier scrolls away with the page, so a compact copy of the
 * name docks under the site header once the dossier's own name has gone under
 * it. The <h1> stays the page's one heading; this copy is hidden from assistive
 * technology.
 */
function nameBar(name: string, office: PublicSeatAssignmentSection | undefined): HTMLElement {
  return h(
    "div",
    { class: "bsc-person__bar", "aria-hidden": "true" },
    h(
      "div",
      { class: "bsc-person__bar-inner" },
      h("span", { class: "bsc-person__bar-name" }, name),
      office ? h("span", { class: "bsc-person__bar-role" }, heldOfficeTitle(office)) : null
    )
  );
}

function revealNameBar(bar: HTMLElement, name: HTMLElement | null, signal: AbortSignal) {
  if (!name || signal.aborted || typeof window === "undefined" || typeof requestAnimationFrame === "undefined") return;
  let frame = 0;
  const measure = () => {
    frame = 0;
    // The bar shows once the name's last line is under the bar's own docked position.
    const top = parseFloat(window.getComputedStyle(bar).top) || 0;
    const shown = name.getBoundingClientRect().bottom < top + 8;
    if (shown) bar.setAttribute("data-shown", "");
    else bar.removeAttribute("data-shown");
  };
  const schedule = () => { if (!frame) frame = requestAnimationFrame(measure); };
  window.addEventListener("scroll", schedule, { passive: true, signal });
  window.addEventListener("resize", schedule, { passive: true, signal });
  signal.addEventListener("abort", () => { if (frame) cancelAnimationFrame(frame); }, { once: true });
  schedule();
}

/**
 * A name is never broken mid-word, so a long one ("Preckwinkle") can outrun the
 * dossier at the stylesheet's size. Step the size down until its longest word fits;
 * the stylesheet's size stands whenever it already does. Re-fit when the card's
 * width changes or the display font arrives.
 */
function fitName(name: HTMLElement | null, signal: AbortSignal) {
  if (!name || signal.aborted || typeof window === "undefined" || !window.getComputedStyle) return;
  const MIN = 28;
  const fit = () => {
    if (signal.aborted || !name.isConnected) return;
    name.style.removeProperty("font-size");
    let size = parseFloat(window.getComputedStyle(name).fontSize) || 0;
    while (name.scrollWidth > name.clientWidth + 1 && size > MIN) {
      size = Math.max(MIN, size - 2);
      name.style.fontSize = `${size}px`;
    }
  };
  let frame = 0;
  const schedule = () => { if (!frame && typeof requestAnimationFrame !== "undefined") frame = requestAnimationFrame(() => { frame = 0; fit(); }); };
  if (typeof ResizeObserver !== "undefined") {
    let width = 0;
    const observer = new ResizeObserver((entries) => {
      const next = Math.round(entries[0]?.contentRect.width ?? 0);
      if (next !== width) { width = next; schedule(); }
    });
    observer.observe(name);
    signal.addEventListener("abort", () => observer.disconnect(), { once: true });
  }
  document.fonts?.ready.then(schedule).catch(() => {});
  schedule();
}

/** Keep the dossier navigation and the record on one active event. */
function syncTimeline(root: HTMLElement): (key: string) => void {
  const select = (key: string) => {
    root.querySelectorAll<HTMLElement>(".bsc-timeline-nav__entry").forEach((b) => {
      if (b.dataset.target === key) b.setAttribute("aria-current", "true");
      else b.removeAttribute("aria-current");
    });
    root.querySelectorAll<HTMLElement>(".bsc-timeline__event").forEach((li) => li.classList.toggle("is-active", li.dataset.key === key));
  };
  let pinnedUntil = 0;
  if ("IntersectionObserver" in window) {
    const visible = new Map<string, number>();
    const io = new IntersectionObserver(
      (records) => {
        if (!root.isConnected) return io.disconnect();
        for (const r of records) visible.set((r.target as HTMLElement).dataset.key!, r.isIntersecting ? r.intersectionRatio : 0);
        if (performance.now() < pinnedUntil) return;
        const best = [...visible].filter(([, ratio]) => ratio > 0).sort((a, b) => b[1] - a[1])[0];
        if (best) select(best[0]);
      },
      { threshold: [0, 0.25, 0.5, 0.75, 1], rootMargin: "-10% 0px -40% 0px" }
    );
    // Observe after insertion into the document.
    queueMicrotask(() => root.querySelectorAll(".bsc-timeline__event").forEach((li) => io.observe(li)));
  }
  return (key: string) => {
    pinnedUntil = performance.now() + 600;
    select(key);
    const target = root.querySelector<HTMLElement>(`#bsc-${CSS.escape(key)}`);
    if (!target) return;
    target.scrollIntoView({ block: "start" });
    const card = target.querySelector<HTMLElement>(".bsc-timeline__card");
    card?.setAttribute("tabindex", "-1");
    card?.focus({ preventScroll: true });
  };
}

/**
 * Offer "Compare candidates" beside a campaign's answers only when its Race
 * has another candidate with a completed Response. That needs the cycle
 * read, so it arrives after the page renders and never blocks it.
 */
async function addCompareControls(root: HTMLElement, page: PublicPersonPage, self: string, ctx: ToolContext, region: HTMLElement) {
  const campaigns = page.candidacies.filter(
    (c) => c.cycle?.publicUrl && c.race && c.questionnaire?.status === "completed" && c.questionnaire.answers
  );
  for (const cycleSlug of new Set(campaigns.map((c) => c.cycle!.slug))) {
    const result = await ctx.api.getCycle(cycleSlug);
    if (!result.ok || !root.isConnected) continue;
    for (const c of campaigns.filter((x) => x.cycle!.slug === cycleSlug)) {
      const others = result.data.ballots
        .flatMap((b) => b.races)
        .filter((r) => r.slug === c.race!.slug)
        .flatMap((r) => r.candidates)
        .some((x) => x.responseStatus === "completed" && personSlug(x.publicUrl) !== self);
      if (!others) continue;
      const slot = root.querySelector(
        `.bsc-campaign[data-cycle-slug="${CSS.escape(cycleSlug)}"][data-race-slug="${CSS.escape(c.race!.slug)}"] .bsc-questionnaire__actions`
      );
      if (!slot || slot.childElementCount) continue;
      const button = h("button", { type: "button", class: "bsc-questionnaire__compare" }, "Compare candidates");
      button.addEventListener("click", () =>
        openComparison(region, button, c.race!.name, {
          api: ctx.api,
          page: ctx.pageUrl(),
          cycle: cycleSlug,
          race: c.race!.slug,
          highlightPerson: self,
        })
      );
      slot.append(button);
    }
  }
}

export const renderPerson: ViewRenderer<PersonView> = async (region, view, ctx) => {
  const root = h("div", { class: "bsc-person" });
  region.replaceChildren(root);
  renderState("loading", root, { subject: "person", headingLevel: 1 });

  const result = await ctx.api.getPerson(view.person);
  const page = ctx.pageUrl();
  if (!result.ok) {
    if (result.reason === "not-found") {
      // A Webflow profile for a slug Backbone does not publish is a mistyped
      // or premature profile — say so to whoever is debugging the page.
      warnOrphans(new Set(), document, [view.person]);
      renderState("not-found", root, { subject: "person", headingLevel: 1, directoryHref: directoryHref(page) });
      return "Person not found";
    }
    renderState("unavailable", root, { subject: "person", headingLevel: 1, onRetry: () => ctx.rerender() });
    return "Profile unavailable";
  }

  const data = result.data;
  const name = personName(data.person);
  const entries = timelineEntries(data.candidacies, data.seatAssignments, localToday());
  const layout = h("div", { class: "bsc-person__layout" });
  const back = returnLink(view, page, ctx.myBallot());
  const bar = nameBar(name, data.seatAssignments[0]);
  root.replaceChildren(...(back ? [bar, back, layout] : [bar, layout]));

  const select = syncTimeline(root);
  layout.append(
    renderDossier({
      name,
      // Profiles show only for a Person Backbone reports as public — which
      // this one is, or the read above would have 404ed.
      profile: getProfile(view.person),
      office: data.seatAssignments[0],
      entries,
      onSelect: select,
    }),
    h("div", { class: "bsc-person__record" }, renderTimeline(entries, page, name))
  );

  const dossierName = layout.querySelector<HTMLElement>(".bsc-dossier__name");
  fitName(dossierName, ctx.viewSignal());
  revealNameBar(bar, dossierName, ctx.viewSignal());
  void addCompareControls(root, data, view.person, ctx, region);
  return name;
};
